import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { HolidayService } from '../../../core/services/holiday.service';
import { showAlertDialog } from '../../../shared/helpers/showAlertDialog';
import { AlertType } from '../../../shared/models/alertType';
import {
  HolidayListFilters,
  HolidayListItemDto,
  HolidayMunicipalityOption,
  HolidayOrderBy,
  HolidayType,
} from '../../../shared/models/holiday';
import { HolidayFormDialogComponent } from './holiday-form-dialog.component';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
  selector: 'app-manage-holidays-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatDialogModule, MatIconModule, NgSelectComponent],
  templateUrl: './manage-holidays-dialog.component.html',
  styleUrl: './manage-holidays-dialog.component.scss',
})
export class ManageHolidaysDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly holidayTypes: HolidayType[] = ['Nacional', 'Estadual', 'Municipal'];
  readonly orderByOptions: Array<{ value: HolidayOrderBy; label: string }> = [
    { value: 'DateAdded', label: 'Data de Adição' },
    { value: 'HolidayDate', label: 'Data do Feriado' },
  ];

  readonly searchControl = new FormControl('', { nonNullable: true });

  holidays: HolidayListItemDto[] = [];
  statesOptions: string[] = [];
  municipalitiesOptions: HolidayMunicipalityOption[] = [];

  selectedTypes: HolidayType[] = [];
  selectedStates: string[] = [];
  selectedCityIbgeCodes: number[] = [];
  selectedOrderBy: HolidayOrderBy = 'DateAdded';

  isLoadingList = false;
  isLoadingOptions = false;

  private listRequestId = 0;
  private optionsRequestId = 0;

  page = 1;
  readonly pageSize = 10;
  totalCount = 0;

  constructor(
    private readonly dialogRef: MatDialogRef<ManageHolidaysDialogComponent>,
    private readonly holidayService: HolidayService,
    private readonly dialog: MatDialog,
  ) {
    this.dialogRef.updateSize('1200px');
  }

  ngOnInit(): void {
    this.setupSearchWatcher();
    this.loadFilterOptions();
    this.loadList();
  }

  close(): void {
    this.dialogRef.close();
  }

  isTypeChecked(type: HolidayType): boolean {
    return this.selectedTypes.includes(type);
  }

  toggleType(type: HolidayType): void {
    this.selectedTypes = this.isTypeChecked(type)
      ? this.selectedTypes.filter((item) => item !== type)
      : [...this.selectedTypes, type];

    this.resetToFirstPage();
    this.loadList();
  }

  onStatesChange(states: string[] | null): void {
    this.selectedStates = states ?? [];
    this.selectedCityIbgeCodes = [];
    this.resetToFirstPage();
    this.loadFilterOptions(this.selectedStates);
    this.loadList();
  }

  onMunicipalitiesChange(codes: number[] | null): void {
    this.selectedCityIbgeCodes = codes ?? [];
    this.resetToFirstPage();
    this.loadList();
  }

  onOrderByChange(orderBy: HolidayOrderBy): void {
    this.selectedOrderBy = orderBy;
    this.resetToFirstPage();
    this.loadList();
  }

  openNewHoliday(): void {
    this.dialog
      .open(HolidayFormDialogComponent, {
        data: {
          mode: 'create',
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result?: { updated?: boolean }) => {
        if (!result?.updated) {
          return;
        }

        this.resetToFirstPage();
        this.loadFilterOptions(this.selectedStates);
        this.loadList();
      });
  }

  openEditHoliday(holidayId: number): void {
    this.dialog
      .open(HolidayFormDialogComponent, {
        data: {
          mode: 'edit',
          holidayId,
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result?: { updated?: boolean }) => {
        if (!result?.updated) {
          return;
        }

        this.loadFilterOptions(this.selectedStates);
        this.loadList();
      });
  }

  deleteHoliday(holiday: HolidayListItemDto): void {
    const formattedDate = this.formatDate(holiday.date);
    const message = `Você está prestes a excluir o feriado <strong>${holiday.reason}</strong> (${formattedDate}). Esta ação é irreversível.`;

    showAlertDialog(this.dialog, AlertType.Warning, 'Excluir feriado?', message, 'Excluir', 'Cancelar', false, [], true)
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }

        this.holidayService.delete(holiday.id).subscribe({
          next: () => {
            if (this.holidays.length === 1 && this.page > 1) {
              this.page -= 1;
            }

            this.loadFilterOptions(this.selectedStates);
            this.loadList();
            showAlertDialog(this.dialog, AlertType.Success, 'Sucesso!', 'Feriado excluído com sucesso.');
          },
          error: () => {
            showAlertDialog(this.dialog, AlertType.Error, 'Erro', 'Não foi possível excluir o feriado. Tente novamente.');
          },
        });
      });
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) {
      return;
    }

    this.page = page;
    this.loadList();
  }

  get hasResults(): boolean {
    return this.holidays.length > 0;
  }

  get startIndex(): number {
    if (this.totalCount === 0) {
      return 0;
    }

    return (this.page - 1) * this.pageSize + 1;
  }

  get endIndex(): number {
    return Math.min(this.page * this.pageSize, this.totalCount);
  }

  get totalPages(): number {
    if (this.totalCount === 0) {
      return 1;
    }

    return Math.ceil(this.totalCount / this.pageSize);
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;

    if (total <= 3) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    if (this.page <= 2) {
      return [1, 2, 3];
    }

    if (this.page >= total - 1) {
      return [total - 2, total - 1, total];
    }

    return [this.page - 1, this.page, this.page + 1];
  }

  typeBadgeClass(type: HolidayType): string {
    if (type === 'Nacional') {
      return 'badge-nacional';
    }

    if (type === 'Estadual') {
      return 'badge-estadual';
    }

    return 'badge-municipal';
  }

  formatDate(date: string): string {
    const [year, month, day] = date.split('-').map(Number);
    const parsedDate = new Date(year, (month || 1) - 1, day || 1);

    const formatted = new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'long',
    }).format(parsedDate);

    const [dayText, middle, monthText] = formatted.split(' ');
    const monthCapitalized = monthText ? `${monthText.charAt(0).toUpperCase()}${monthText.slice(1)}` : '';
    return `${dayText} ${middle ?? 'de'} ${monthCapitalized}`;
  }

  private setupSearchWatcher(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.resetToFirstPage();
        this.loadList();
      });
  }

  private loadFilterOptions(states?: string[]): void {
    const requestId = ++this.optionsRequestId;
    this.isLoadingOptions = true;

    this.holidayService
      .getFilterOptions(states)
      .pipe(
        finalize(() => {
          if (requestId === this.optionsRequestId) {
            this.isLoadingOptions = false;
          }
        }),
      )
      .subscribe({
        next: (options) => {
          if (requestId !== this.optionsRequestId) {
            return;
          }

          this.statesOptions = [...options.states].sort((a, b) => a.localeCompare(b));
          this.municipalitiesOptions = [...options.municipalities].sort((a, b) => a.name.localeCompare(b.name));

          const availableCodes = new Set(this.municipalitiesOptions.map((item) => item.ibgeCode));
          this.selectedCityIbgeCodes = this.selectedCityIbgeCodes.filter((code) => availableCodes.has(code));
        },
        error: () => {
          if (requestId !== this.optionsRequestId) {
            return;
          }

          this.statesOptions = [];
          this.municipalitiesOptions = [];
        },
      });
  }

  private loadList(): void {
    const requestId = ++this.listRequestId;
    this.isLoadingList = true;
    const filters: HolidayListFilters = {
      page: this.page,
      pageSize: this.pageSize,
      types: this.selectedTypes,
      states: this.selectedStates,
      cityIbgeCodes: this.selectedCityIbgeCodes,
      search: this.searchControl.value,
      orderBy: this.selectedOrderBy,
    };

    this.holidayService
      .getList(filters)
      .pipe(
        finalize(() => {
          if (requestId === this.listRequestId) {
            this.isLoadingList = false;
          }
        }),
      )
      .subscribe({
        next: (result) => {
          if (requestId !== this.listRequestId) {
            return;
          }

          this.holidays = result.items;
          this.totalCount = result.totalCount;
          this.page = result.page;
        },
        error: (error: HttpErrorResponse) => {
          if (requestId !== this.listRequestId) {
            return;
          }

          this.holidays = [];
          this.totalCount = 0;

          if (error.status !== 0) {
            showAlertDialog(this.dialog, AlertType.Error, 'Erro', 'Não foi possível carregar os feriados.');
          }
        },
      });
  }

  private resetToFirstPage(): void {
    this.page = 1;
  }
}
